import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { 
  Link2, X, Search, FolderOpen, Users, Plus, Check 
} from 'lucide-react';

// Entity link badge
const EntityLinkBadge = ({ type, name, onRemove }) => {
  const config = {
    project: { icon: FolderOpen, color: 'bg-blue-100 text-blue-700' },
    employee: { icon: Users, color: 'bg-purple-100 text-purple-700' },
  };
  const { icon: Icon, color } = config[type] || config.project;

  return (
    <Badge className={`${color} flex items-center gap-1 pr-1`}>
      <Icon className="h-3 w-3" />
      <span className="max-w-[120px] truncate">{name}</span>
      {onRemove && (
        <button 
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="ml-1 p-0.5 rounded hover:bg-white/50"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </Badge>
  );
};

// Entity link selector dialog
const EntityLinkDialog = ({ 
  open, 
  onOpenChange, 
  type, // 'project' | 'employee'
  items = [], // All available items
  selectedIds = [],
  onSave,
  title
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [localSelected, setLocalSelected] = useState(selectedIds);

  const filteredItems = items.filter(item => {
    const name = item.name || item.title || '';
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const toggleItem = (id) => {
    setLocalSelected(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleSave = () => {
    onSave(localSelected);
    onOpenChange(false);
  };

  React.useEffect(() => {
    if (open) {
      setLocalSelected(selectedIds);
    }
  }, [open, selectedIds]);

  const Icon = type === 'project' ? FolderOpen : Users;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5" />
            {title || `Link ${type === 'project' ? 'Projects' : 'Employees'}`}
          </DialogTitle>
          <DialogDescription className="sr-only">{t('auto.iso.dialog')}</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={`Search ${type}s...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Selected count */}
          <div className="text-sm text-gray-500">
            {localSelected.length} selected
          </div>

          {/* Items list */}
          <div className="max-h-[300px] overflow-y-auto border rounded-md">
            {filteredItems.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                No {type}s found
              </div>
            ) : (
              filteredItems.map(item => {
                const isSelected = localSelected.includes(item.id);
                return (
                  <div
                    key={item.id}
                    onClick={() => toggleItem(item.id)}
                    className={`p-3 border-b last:border-0 cursor-pointer flex items-center justify-between hover:bg-gray-50 ${
                      isSelected ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className={`h-4 w-4 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                      <div>
                        <p className="font-medium text-sm">{item.name || item.title}</p>
                        {type === 'project' && item.customer_name && (
                          <p className="text-xs text-gray-500">{item.customer_name}</p>
                        )}
                        {type === 'employee' && item.department && (
                          <p className="text-xs text-gray-500">{item.department}</p>
                        )}
                      </div>
                    </div>
                    {isSelected && (
                      <Check className="h-4 w-4 text-blue-600" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">
            {t('auto.iso.cancel')}
          </Button>
          <Button onClick={handleSave} data-testid="dialog-save-btn">
            <Check className="h-4 w-4 mr-2" />
            {t('auto.iso.saveLinks')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main entity linker component
const EntityLinker = ({
  linkedProjectIds = [],
  linkedEmployeeIds = [],
  projects = [],
  employees = [],
  onProjectsChange,
  onEmployeesChange,
  readOnly = false,
  compact = false,
}) => {
  const { t } = useTranslation();
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [showEmployeeDialog, setShowEmployeeDialog] = useState(false);

  // Get names for display
  const getProjectName = (id) => {
    const proj = projects.find(p => p.id === id);
    return proj?.name || 'Unknown Project';
  };

  const getEmployeeName = (id) => {
    const emp = employees.find(e => e.id === id);
    return emp?.name || 'Unknown Employee';
  };

  // Remove handlers
  const removeProject = (id) => {
    if (onProjectsChange) {
      onProjectsChange(linkedProjectIds.filter(i => i !== id));
    }
  };

  const removeEmployee = (id) => {
    if (onEmployeesChange) {
      onEmployeesChange(linkedEmployeeIds.filter(i => i !== id));
    }
  };

  if (compact) {
    return (
      <div className="flex flex-wrap gap-2">
        {linkedProjectIds.map(id => (
          <EntityLinkBadge 
            key={`proj-${id}`} 
            type="project" 
            name={getProjectName(id)} 
            onRemove={readOnly ? undefined : () => removeProject(id)}
          />
        ))}
        {linkedEmployeeIds.map(id => (
          <EntityLinkBadge 
            key={`emp-${id}`} 
            type="employee" 
            name={getEmployeeName(id)}
            onRemove={readOnly ? undefined : () => removeEmployee(id)}
          />
        ))}
        {!readOnly && (linkedProjectIds.length > 0 || linkedEmployeeIds.length > 0) && (
          <span className="text-gray-400">|</span>
        )}
        {!readOnly && (
          <>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 px-2"
              onClick={() => setShowProjectDialog(true)}
            >
              <FolderOpen className="h-3 w-3 mr-1" />
              <span className="text-xs">{t('auto.iso.projects')}</span>
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 px-2"
              onClick={() => setShowEmployeeDialog(true)}
            >
              <Users className="h-3 w-3 mr-1" />
              <span className="text-xs">{t('auto.iso.employees')}</span>
            </Button>
          </>
        )}

        {/* Dialogs */}
        <EntityLinkDialog
          open={showProjectDialog}
          onOpenChange={setShowProjectDialog}
          type="project"
          items={projects}
          selectedIds={linkedProjectIds}
          onSave={onProjectsChange}
        />
        <EntityLinkDialog
          open={showEmployeeDialog}
          onOpenChange={setShowEmployeeDialog}
          type="employee"
          items={employees}
          selectedIds={linkedEmployeeIds}
          onSave={onEmployeesChange}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Projects Section */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <FolderOpen className="h-4 w-4 text-blue-600" />
            {t('auto.iso.linkedProjects')}
          </label>
          {!readOnly && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowProjectDialog(true)}
            >
              <Plus className="h-3 w-3 mr-1" />
              {t('auto.iso.add')}
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-2 min-h-[32px]">
          {linkedProjectIds.length === 0 ? (
            <span className="text-sm text-gray-400">{t('auto.iso.noLinkedProjects')}</span>
          ) : (
            linkedProjectIds.map(id => (
              <EntityLinkBadge 
                key={id} 
                type="project" 
                name={getProjectName(id)}
                onRemove={readOnly ? undefined : () => removeProject(id)}
              />
            ))
          )}
        </div>
      </div>

      {/* Employees Section */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <Users className="h-4 w-4 text-purple-600" />
            {t('auto.iso.linkedEmployees')}
          </label>
          {!readOnly && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowEmployeeDialog(true)}
            >
              <Plus className="h-3 w-3 mr-1" />
              {t('auto.iso.add')}
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-2 min-h-[32px]">
          {linkedEmployeeIds.length === 0 ? (
            <span className="text-sm text-gray-400">{t('auto.iso.noLinkedEmployees')}</span>
          ) : (
            linkedEmployeeIds.map(id => (
              <EntityLinkBadge 
                key={id} 
                type="employee" 
                name={getEmployeeName(id)}
                onRemove={readOnly ? undefined : () => removeEmployee(id)}
              />
            ))
          )}
        </div>
      </div>

      {/* Dialogs */}
      <EntityLinkDialog
        open={showProjectDialog}
        onOpenChange={setShowProjectDialog}
        type="project"
        items={projects}
        selectedIds={linkedProjectIds}
        onSave={onProjectsChange}
      />
      <EntityLinkDialog
        open={showEmployeeDialog}
        onOpenChange={setShowEmployeeDialog}
        type="employee"
        items={employees}
        selectedIds={linkedEmployeeIds}
        onSave={onEmployeesChange}
      />
    </div>
  );
};

export default EntityLinker;
export { EntityLinkBadge, EntityLinkDialog };
